import * as React from 'react';

import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import {getCustomFieldValueForMutationFromString} from 'shells/row/custom-field-row/helpers';

import type {EntityAttribute, CustomFieldValueInput} from 'nutshell-graphql-types';

export interface FieldError {
    fieldName: string;
    error: string | null | undefined;
}

export type UpdateCustomField = (args: {
    fieldName: string;
    value: CustomFieldValueInput;
}) => Promise<void>;

export function useCustomFieldHandlers(
    updateCustomField: UpdateCustomField,
    onSuccess?: () => void | Promise<void>
) {
    const [fieldErrors, setFieldErrors] = React.useState<FieldError[]>([]);

    function handleCustomFieldMutation(
        fieldName: string,
        value: CustomFieldValueInput
    ): Promise<any> {
        return updateCustomField({fieldName, value})
            .then(() => {
                onSuccess?.();
            })
            .catch((e) => {
                setErrorForField({fieldName: fieldName, error: safelyGetGraphQLErrorMessage(e)});
                throw e;
            });
    }

    // Handle text field updates (text, string, date, email, phone, location, url)
    function handleTextFieldUpdate(
        newValue: string | null | undefined,
        field: EntityAttribute
    ): Promise<any> {
        const value = getCustomFieldValueForMutationFromString(field.attribute.type, newValue);

        return handleCustomFieldMutation(field.attribute.name, value);
    }

    function handleEnumUpdate(
        newValue: string[] | null | undefined,
        field: EntityAttribute
    ): Promise<any> {
        let value: CustomFieldValueInput;

        // @ts-expect-error
        if (!field.allowMultiple) {
            value = getCustomFieldValueForMutationFromString(
                field.attribute.type,
                newValue && newValue[0]
            );
        } else {
            value = {
                enumMultiple: newValue,
            };
        }

        return handleCustomFieldMutation(field.attribute.name, value);
    }

    function handleCurrencyUpdate(
        currencyShortname: string,
        value: number | null | undefined,
        field: EntityAttribute
    ): Promise<any> {
        // @ts-expect-error
        if (isNaN(value)) {
            setErrorForField({
                fieldName: field.attribute.name,
                error: 'Amount must be a number',
            });

            return Promise.reject();
        }

        const currencyValue: CustomFieldValueInput =
            value != null
                ? {
                      currency: {
                          currencyShortname,
                          value,
                      },
                  }
                : {
                      currency: null,
                  };

        return handleCustomFieldMutation(field.attribute.name, currencyValue);
    }

    function handleAddressUpdate(
        address1: string,
        address2: string,
        address3: string,
        city: string,
        state: string,
        postalCode: string,
        country: string,
        field: EntityAttribute
    ) {
        return handleCustomFieldMutation(field.attribute.name, {
            location: {
                address_1: address1 || null,
                address_2: address2 || null,
                address_3: address3 || null,
                city: city || null,
                state: state || null,
                postalCode: postalCode || null,
                country: country || null,
            },
        });
    }

    const handlePhoneUpdate = (
        numberFormattedWithCountryCode: string,
        extension: string,
        field: EntityAttribute
    ): Promise<any> => {
        return handleCustomFieldMutation(field.attribute.name, {
            phone: {
                countryCodeAndNumber: numberFormattedWithCountryCode || null,
                extension: extension || null,
            },
        });
    };

    const setErrorForField = (fieldError: FieldError) => {
        if (fieldError.error === null) {
            setFieldErrors((prevFieldErrors) => {
                return prevFieldErrors.filter(
                    (fError) => fError.fieldName !== fieldError.fieldName
                );
            });

            return;
        }

        setFieldErrors((prevFieldErrors) => {
            const newFieldErrors = prevFieldErrors.filter(
                (fError) => fError.fieldName !== fieldError.fieldName
            );

            return [...newFieldErrors, fieldError];
        });
    };

    return {
        handleTextFieldUpdate,
        handleEnumUpdate,
        handleCurrencyUpdate,
        handleAddressUpdate,
        handlePhoneUpdate,
        fieldErrors,
        setErrorForField,
    };
}
